import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment/moment'
import i18n from 'simple-react-i18n'
import { groupBy, orderBy } from 'lodash'
import ReactDOMServer from 'react-dom/server'
import PluviometryAction from '../../../actions/PluviometryAction'
import PluviometerDto from '../../../dto/PluviometerDto'
import DtoPluvioMeasure from '../../../dto/measures/DtoPluvioMeasure'
import DtoPluviometerStats from '../../../dto/DtoPluviometerStats'
import ProgressCard from '../../../../components/card/ProgressCard'
import MessageCard from '../../../../components/card/MessageCard'
import EChartV6Pluvio from '../../../../components/echart/EChartV6Pluvio'
import {
    exportExcelIcon,
    exportPictureIcon,
    getAxisIntervalFormatter,
    histogramIcon,
    lineIcon,
} from '../../../../components/echart/EChartUtils'
import { genericPromise2 } from '../../../../utils/ActionUtils'
import ApplicationConf from '../../../../conf/ApplicationConf'
import { exportFile } from '../../../../utils/ExportDataUtil'
import { DEFROUNDVALUE, nFormatter, round } from '../../../../utils/NumberUtil'
import { getExportFormat } from '../../../../utils/StringUtil'
import { getDate, getMonthYear, getStartMonth } from '../../../../utils/DateUtil'
import { statusIcon } from '../../../../utils/StatusUtil'
import { DATATYPE_RAIN } from '../../../../utils/PluviometryUtils'
import { SUM_MONTH } from '../../../constants/PluvioOptionConstant'
import { Grid } from '@mui/material'

const NORMALE_CUMUL_MONTH = 'NORMALE_CUMUL_MONTH'
const NORMALE_MONTH = 'NORMALE_MONTH'
const LINE = 'line'
const BAR = 'bar'
const CHART_HEIGHT = 500

const buildSeries = (type, measures, color, name) => ({
    name,
    type,
    barWidth: 4,
    showSymbol: false,
    symbol: 'none',
    connectNulls: false,
    itemStyle: { color },
    lineStyle: { color },
    data: measures.map(m => ({
        value: [m.date, m.value],
        rawMeasure: m,
    })),
})

const Graphic = ({
    pluviometer,
    data,
    minDate,
    maxDate,
}) => {
    const pluviometerStatistics = useSelector(state => state.PluviometryReducer.pluviometerStatistics)
    const pluviometryDataTypes = useSelector(state => state.PluviometryReducer.pluviometryDataTypes)

    const [typeLineValue, setTypeLineValue] = useState(BAR)

    const unit = pluviometerStatistics.find(p => p.typeId === DATATYPE_RAIN)?.unit
    const unitLabel = unit ? `[${unit}]` : ''
    const roundValue = pluviometryDataTypes.find(pdt => pdt.id === DATATYPE_RAIN)?.numberDecimal || DEFROUNDVALUE

    const axisLabelObj = getAxisIntervalFormatter(moment(maxDate), moment(minDate))

    const monthlyName = `${pluviometer.name} | ${i18n.monthlyRainfall}`
    const cumulativeName = `${pluviometer.name} | ${i18n.cumulativeDifferencesMm}`

    const monthlySeries = buildSeries(typeLineValue, data[0].measures, 'blue', monthlyName)
    const cumulativeSeries = buildSeries('line', data[1].measures, 'red', cumulativeName)

    const buildExportRows = (normales) => {
        const normalesByMonth = groupBy(normales, m => moment(m[2]).month())
        return data[0].measures.reduce((acc, d) => {
            const normale = normalesByMonth[moment(d.date).month()][0]
            const diff = d.value - normale[4]
            const ecartPercent = ((d.value - normale[4]) / normale[4]) * 100
            const cumul = acc.cumul + diff
            const ecartPercentTotal = acc.ecartPercentTotal + ecartPercent
            const row = {
                stationCode: { value: pluviometer.code },
                stationName: { value: pluviometer.name },
                date: { value: getMonthYear(d.date), format: 'MM/yyyy', cellType: 'date' },
                [i18n.monthlyRainfall]: { value: round(d.value, roundValue), format: getExportFormat(roundValue), cellType: 'number' },
                [i18n.normalMonths]: { value: round(normale[4], roundValue), format: getExportFormat(roundValue), cellType: 'number' },
                [i18n.deviationNormalsMm]: { value: round(diff, roundValue), format: getExportFormat(roundValue), cellType: 'number' },
                [i18n.deviationNormalsPourcent]: { value: round(ecartPercent), format: '0.00', cellType: 'number' },
                [i18n.cumulativeDifferencesMm]: { value: round(cumul, roundValue), format: getExportFormat(roundValue), cellType: 'number' },
                [i18n.cumulativeDifferencesPourcent]: { value: round(ecartPercentTotal), format: '0.00', cellType: 'number' },
                headers: ['stationCode', 'stationName', 'date', [i18n.monthlyRainfall], [i18n.normalMonths], [i18n.deviationNormalsMm], [i18n.deviationNormalsPourcent], [i18n.cumulativeDifferencesMm], [i18n.cumulativeDifferencesPourcent]],
            }
            return { cumul, ecartPercentTotal, rows: [...acc.rows, row] }
        }, { cumul: 0, ecartPercentTotal: 0, rows: [] }).rows
    }

    const handleExport = () => {
        const options = {
            method: 'POST',
            body: {
                stationId: pluviometer.id,
                dataType: DATATYPE_RAIN,
                groupFunc: NORMALE_MONTH,
                chartMode: false,
                chartStartDate: minDate,
            },
        }
        genericPromise2(ApplicationConf.pluviometry.chartMeasures(), options).then(normales => {
            exportFile({
                data: buildExportRows(normales),
                exportType: 'xlsx',
                titleFile: i18n.overview,
            })
        })
    }

    const option = {
        title: {
            text: `[${pluviometer.code}]${pluviometer.name ? `   -   [${pluviometer.name}]` : ''}`,
            left: 'center',
            top: 5,
        },
        tooltip: {
            trigger: 'axis',
            formatter: params => {
                const refDate = params[0]?.data?.rawMeasure?.date
                const date = getDate(refDate)
                const lines = orderBy(params.map(p => ({
                    seriesIndex: p.seriesIndex,
                    marker: p.marker,
                    seriesName: p.seriesName,
                    value: p.data?.rawMeasure?.value,
                    status: ReactDOMServer.renderToString(statusIcon(p.data?.rawMeasure, 20)),
                })), 'seriesIndex', 'asc')
                const body = lines
                    .map(o => `<span style="display:flex;align-items:center;">${o.marker} ${o.seriesName} : ${round(o.value, 2)} ${unitLabel}</span>`)
                    .join('')
                return `${date} <br /> ${body} `
            },
        },
        grid: [{ top: 35, right: '2%', bottom: 110, left: 60 }],
        xAxis: [{
            type: 'time',
            position: 'bottom',
            min: getStartMonth(minDate).valueOf(),
            max: maxDate ?? moment().valueOf(),
            gridIndex: 0,
            interval: axisLabelObj.interval,
            axisLabel: { show: true, formatter: v => String(axisLabelObj.formatter(v)) },
            axisLine: { onZero: true },
            splitLine: { show: true },
        }],
        yAxis: [{
            type: 'value',
            name: `${i18n.comparisonNormal} ${unitLabel}`,
            nameLocation: 'middle',
            nameGap: 40,
            gridIndex: 0,
            splitLine: { show: true },
            axisLabel: { formatter: nFormatter },
            axisLine: { onZero: true },
        }],
        axisPointer: { link: [{ xAxisIndex: 'all' }] },
        dataZoom: [
            { type: 'inside', xAxisIndex: 0, filterMode: 'none' },
            { type: 'slider', xAxisIndex: 0, bottom: 5, height: 18, filterMode: 'none' },
        ],
        legend: { bottom: 35, type: 'scroll' },
        toolbox: {
            show: true,
            right: 65,
            feature: {
                saveAsImage: { title: i18n.export, icon: exportPictureIcon },
                myToolExport: {
                    show: true,
                    title: i18n.excelExport,
                    icon: exportExcelIcon,
                    onclick: handleExport,
                },
                myToolLine: {
                    show: true,
                    title: i18n.curve,
                    icon: lineIcon,
                    onclick: () => setTypeLineValue(LINE),
                },
                myToolBar: {
                    show: true,
                    title: i18n.histogram,
                    icon: histogramIcon,
                    onclick: () => setTypeLineValue(BAR),
                },
            },
        },
        series: [monthlySeries, cumulativeSeries],
    }

    return (
        <div className='padding-top-1'>
            <EChartV6Pluvio option={option} style={{ height: CHART_HEIGHT }} />
        </div>
    )
}

Graphic.propTypes = {
    pluviometer: PropTypes.instanceOf(PluviometerDto),
    data: PropTypes.arrayOf(PropTypes.shape({
        measures: PropTypes.arrayOf(PropTypes.instanceOf(DtoPluvioMeasure)),
    })),
    minDate: PropTypes.number,
    maxDate: PropTypes.number,
}

const GraphComparisonNormal = ({
    pluviometer,
    stat,
    dateComparedNormal,
}) => {
    const [dataLoaded, setDataLoaded] = useState(false)
    const [progress, setProgress] = useState(0)

    const dispatch = useDispatch()
    const pluvioMeasures = useSelector(state => state.PluviometryReducer.pluvioMeasures)

    useEffect(() => {
        setDataLoaded(false)
        const startDate = dateComparedNormal || moment().valueOf()
        const sumMonthInput = {
            stationId: pluviometer.id,
            dataType: DATATYPE_RAIN,
            groupFunc: SUM_MONTH,
            chartMode: false,
            startDate,
        }
        const normalCumulMonthInput = {
            stationId: pluviometer.id,
            dataType: DATATYPE_RAIN,
            groupFunc: NORMALE_CUMUL_MONTH,
            chartMode: false,
            chartStartDate: startDate,
        }
        dispatch(PluviometryAction.loadPluvioChartChronicMeasures(
            [sumMonthInput, normalCumulMonthInput],
            setProgress,
        )).then(() => setDataLoaded(true))
    }, [dispatch, pluviometer, stat, dateComparedNormal])

    if (pluvioMeasures.length < 2) {
        return (
            <div style={{ paddingTop: 45 }}>
                <MessageCard>{i18n.noDataToDisplay}</MessageCard>
            </div>
        )
    }

    const data = [
        { measures: pluvioMeasures[0].measures, typeMeasure: SUM_MONTH },
        { measures: pluvioMeasures[1].measures, typeMeasure: NORMALE_CUMUL_MONTH },
    ]
    const totalMeasures = pluvioMeasures[0].measures.length + pluvioMeasures[1].measures.length
    const dateNow = moment().valueOf()

    return (
        <>
            {!dataLoaded && (
                <ProgressCard progress={progress} />
            )}
            {!totalMeasures && (
                <MessageCard>{i18n.noDataToDisplay}</MessageCard>
            )}
            {dataLoaded && totalMeasures > 0 && (
                <Graphic
                    pluviometer={pluviometer}
                    data={data}
                    minDate={dateComparedNormal || dateNow}
                    maxDate={dateNow}
                />
            )}
        </>
    )
}

GraphComparisonNormal.propTypes = {
    pluviometer: PropTypes.instanceOf(PluviometerDto),
    stat: PropTypes.instanceOf(DtoPluviometerStats),
    dateComparedNormal: PropTypes.number,
}

export default GraphComparisonNormal
